import React from 'react'

// ── Design tokens using CSS custom properties ─────────────────────────────────
// All values read from CSS variables so [data-theme="light"] on <html>
// switches the ENTIRE app instantly — no React re-render needed for colours.
export const T = {
  // Brand + semantic colours — follow CSS variables per theme
  canyon:      'var(--canyon)',
  canyon80:    'var(--canyon-80)',
  canyonDim:   'var(--canyon-dim)',
  canyonGlow:  'var(--canyon-glow)',
  olivine:     'var(--olivine)',
  olivineDim:  'var(--olivine-dim)',
  serpentine:  'var(--serpentine)',
  serpDim:     'var(--serp-dim)',
  amber:       'var(--amber)',
  amberDim:    'var(--amber-dim)',
  sand:        'var(--sand)',
  sapphire:    '#4A90D9',   // ML/AI feature accent — blue
  offWhite:    'var(--off-white)',
  rock:        'var(--rock)',
  earth:       'var(--earth)',
  shale:       'var(--shale)',
  // Surfaces — read from CSS variables (change with theme)
  surface0:    'var(--surface-0)',
  surface1:    'var(--surface-1)',
  surface2:    'var(--surface-2)',
  surface3:    'var(--surface-3)',
  surface4:    'var(--surface-4)',
  surface5:    'var(--surface-5)',
  // Text — read from CSS variables
  textPrimary:   'var(--text-primary)',
  textSecondary: 'var(--text-secondary)',
  textMuted:     'var(--text-muted)',
  // Borders — read from CSS variables
  border:   'var(--border)',
  border2:  'var(--border-2)',
  border3:  'var(--border-3)',
  // Typography
  // Typography — use CSS var with fallback stack for reliability
  fontDisplay: "var(--font-display, 'Plus Jakarta Sans Variable', 'Plus Jakarta Sans', system-ui, sans-serif)",
  fontBody:    "var(--font-body, 'Plus Jakarta Sans Variable', 'Plus Jakarta Sans', system-ui, sans-serif)",
  fontMono:    "var(--font-mono, 'DM Mono', 'Fira Code', monospace)",
  // Radii
  r: 10, rSm: 8, rLg: 14, rXl: 20,
}

/** Frosted glass base — used by cards, panels, and modals */
export const glass: React.CSSProperties = {
  position: 'relative',
  background: 'var(--glass-card-bg)',
  backdropFilter: 'blur(var(--glass-blur)) saturate(var(--glass-saturate))',
  WebkitBackdropFilter: 'blur(var(--glass-blur)) saturate(var(--glass-saturate))',
  border: '1px solid var(--glass-card-border)',
  boxShadow: 'var(--glass-card-outer), var(--glass-card-inset)',
}

// ── Style helpers ─────────────────────────────────────────────────────────────

export const card = (extra?: React.CSSProperties): React.CSSProperties => ({
  ...glass,
  borderRadius: 'var(--r-lg)',
  padding: 20,
  ...extra,
})

export const cardStatic = card

export const cardTitle = (extra?: React.CSSProperties): React.CSSProperties => ({
  fontFamily: T.fontDisplay, fontSize: 15, fontWeight: 700,
  letterSpacing: '-0.01em',
  color: 'var(--text-primary)', marginBottom: 12,
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  ...extra,
})

// ── Severity & status maps ─────────────────────────────────────────────────────
// Use explicit hex for severity colours since they're brand constants
const SEV_COLORS: Record<string, [string, string]> = {
  critical: ['#DC7424', 'rgba(220,116,36,0.10)'],
  high:     ['#C9A458', 'rgba(201,164,88,0.14)'],
  medium:   ['#6C7F38', 'rgba(108,127,56,0.15)'],
  low:      ['#B8AA82', 'rgba(184,170,130,0.12)'],
  info:     ['#4A5328', 'rgba(74,83,40,0.18)'],
}

const STATUS_COLORS: Record<string, [string, string]> = {
  open:      ['#DC7424', 'rgba(220,116,36,0.14)'],
  pending:   ['#C9A458', 'rgba(201,164,88,0.14)'],
  approved:  ['#6C7F38', 'rgba(108,127,56,0.15)'],
  rejected:  ['#C0392B', 'rgba(192,57,43,0.12)'],
  completed: ['#6C7F38', 'rgba(108,127,56,0.15)'],
  failed:    ['#C0392B', 'rgba(192,57,43,0.12)'],
  queued:    ['#4A5328', 'rgba(74,83,40,0.18)'],
  running:   ['#C9A458', 'rgba(201,164,88,0.14)'],
  ready:     ['#6C7F38', 'rgba(108,127,56,0.15)'],
  in_review: ['#C9A458', 'rgba(201,164,88,0.14)'],
  inactive:  ['var(--text-muted)', 'rgba(110,102,90,0.12)'],
  active:    ['#6C7F38', 'rgba(108,127,56,0.15)'],
}

// ── Components ────────────────────────────────────────────────────────────────

export function SevPill({ s }: { s: string }) {
  const [color, bg] = SEV_COLORS[s?.toLowerCase()] || SEV_COLORS.low
  return (
    <span style={{ display:'inline-flex', alignItems:'center', gap:4, padding:'3px 9px', borderRadius:20,
      fontSize:10, fontWeight:700, fontFamily:T.fontDisplay, letterSpacing:0.6,
      textTransform:'uppercase', color, background:bg, border:`1px solid ${color}35`,
      backdropFilter: 'blur(10px) saturate(1.2)', WebkitBackdropFilter: 'blur(10px) saturate(1.2)' }}>
      <span style={{ width:5, height:5, borderRadius:'50%', background:color, flexShrink:0 }} />
      {s}
    </span>
  )
}

export function StatusPill({ s }: { s: string }) {
  const [c, bg] = STATUS_COLORS[s?.toLowerCase()] || ['var(--text-muted)', 'rgba(107,101,96,0.12)']
  return (
    <span style={{ display:'inline-flex', padding:'3px 9px', borderRadius:20,
      fontSize:10, fontWeight:700, fontFamily:T.fontDisplay, letterSpacing:0.5,
      textTransform:'uppercase', color:c, background:bg, border:`1px solid ${c}35`,
      backdropFilter: 'blur(10px) saturate(1.2)', WebkitBackdropFilter: 'blur(10px) saturate(1.2)' }}>
      {s?.replace(/_/g,' ')}
    </span>
  )
}

export function TagPill({ children, color }: { children: React.ReactNode; color?: string }) {
  const c = color || '#B8AA82'
  return (
    <span style={{ display:'inline-flex', padding:'2px 8px', borderRadius:4,
      fontSize:11, fontFamily:T.fontMono, fontWeight:500,
      background:`${c}18`, color:c, border:`1px solid ${c}30` }}>
      {children}
    </span>
  )
}

export function GateBanner({ pass, msg }: { pass: boolean; msg: string }) {
  const [c, bg, border] = pass
    ? ['#6C7F38', 'rgba(108,127,56,0.12)', 'rgba(108,127,56,0.35)']
    : ['#DC7424', 'rgba(220,116,36,0.12)', 'rgba(220,116,36,0.35)']
  return (
    <div style={{
      ...glass,
      padding: '12px 18px', borderRadius: 'var(--r-lg)', marginBottom: 18,
      display: 'flex', alignItems: 'center', gap: 12, fontSize: 13, fontWeight: 600,
      color: c,
      background: `color-mix(in srgb, ${bg} 75%, transparent)`,
      border: `1px solid ${border}`,
      boxShadow: 'var(--glass-card-outer), var(--glass-card-inset)',
    }}>
      <span style={{ fontSize: 15 }}>{pass ? '✔' : '✗'}</span>
      <span>{msg}</span>
    </div>
  )
}

export function KpiCard({ label, value, sub, color, icon: _icon, delay=0 }: {
  label:string; value:string|number; sub?:string; color?:string; icon?:string; delay?:number }) {
  const c = color || T.canyon
  return (
    <div className={`fade-up d${Math.min(delay+1,6)}`}
      style={{ ...card(), position:'relative', overflow:'hidden' }}>
      <div style={{ position:'absolute', top:0, right:0, width:100, height:100,
        background:`radial-gradient(circle at top right, ${c}18 0%, transparent 65%)` }} />
      <div style={{ fontSize:10, fontFamily:T.fontDisplay, fontWeight:700, textTransform:'uppercase', letterSpacing:1.1, color:T.textMuted, marginBottom:8 }}>{label}</div>
      <div style={{ fontSize:32, fontWeight:800, fontFamily:T.fontDisplay, color:c, lineHeight:1, marginBottom:6 }}>{value}</div>
      {sub && <div style={{ fontSize:12, color:T.textMuted }}>{sub}</div>}
      <div style={{ position:'absolute', bottom:0, left:0, right:0, height:2, background:`linear-gradient(90deg,${c},transparent)`, opacity:0.6 }} />
    </div>
  )
}

export function DimBar({ dim, score, delay=0 }: { dim:string; score:number; delay?:number }) {
  const color = score>=90?'#6C7F38':score>=80?'#C9A458':'#DC7424'
  const status = score>=90?'OK':score>=80?'WARN':'CRIT'
  return (
    <div className={`fade-up d${Math.min(delay+1,6)}`} style={{ display:'flex', alignItems:'center', gap:10, marginBottom:10 }}>
      <div style={{ width:100, fontSize:12, fontWeight:500, color:T.textSecondary, flexShrink:0 }}>{dim}</div>
      <div style={{ flex:1, height:7, background:T.surface4, borderRadius:4, overflow:'hidden' }}>
        <div style={{ width:`${score}%`, height:'100%',
          background:`linear-gradient(90deg,${color}BB,${color})`, borderRadius:4 }} />
      </div>
      <div style={{ width:40, textAlign:'right', fontSize:12, fontWeight:700, color, fontFamily:T.fontDisplay }}>{score}%</div>
      <span style={{ fontSize:9, padding:'2px 7px', borderRadius:10, fontWeight:700, fontFamily:T.fontDisplay,
        color, background:`${color}18`, border:`1px solid ${color}30`, minWidth:36, textAlign:'center' }}>{status}</span>
    </div>
  )
}

export function Toggle({ checked, onChange }: { checked: boolean; onChange: (next: boolean) => void }) {
  return (
    <label style={{ position:'relative', width:36, height:20, display:'inline-block', cursor:'pointer', flexShrink:0 }}>
      <input type="checkbox" checked={checked} onChange={() => onChange(!checked)} style={{ opacity:0, width:0, height:0, position:'absolute' }} />
      <div style={{ position:'absolute', inset:0,
        background: checked ? `linear-gradient(135deg,${T.canyon},${T.canyon80})` : T.surface4,
        borderRadius:10, transition:'0.25s' }}>
        <div style={{ position:'absolute', top:3, left:checked?17:3, width:14, height:14,
          background: checked ? '#fff' : '#888',
          borderRadius:'50%', transition:'0.25s cubic-bezier(0.16,1,0.3,1)', boxShadow:'0 1px 3px rgba(0,0,0,0.4)' }} />
      </div>
    </label>
  )
}

export function ReadinessRing({ score, size=110 }: { score:number; size?:number }) {
  const r=40, circ=2*Math.PI*r, fill=(score/100)*circ
  const color = score>=85?'#6C7F38':score>=70?'#C9A458':'#DC7424'
  return (
    <div style={{ position:'relative', width:size, height:size, flexShrink:0 }}>
      <svg width={size} height={size} viewBox="0 0 100 100">
        <circle cx="50" cy="50" r={r} fill="none" stroke="var(--surface-4)" strokeWidth={8} />
        <circle cx="50" cy="50" r={r} fill="none" stroke={color} strokeWidth={8}
          strokeDasharray={`${fill} ${circ}`} strokeLinecap="round" strokeDashoffset={circ/4} />
      </svg>
      <div style={{ position:'absolute', inset:0, display:'flex', flexDirection:'column', alignItems:'center', justifyContent:'center' }}>
        <div style={{ fontFamily:T.fontDisplay, fontSize:22, fontWeight:800, color, lineHeight:1 }}>{score}%</div>
        <div style={{ fontSize:9, color:T.textMuted, fontFamily:T.fontDisplay, letterSpacing:0.5, textTransform:'uppercase', marginTop:1 }}>Readiness</div>
      </div>
    </div>
  )
}

export function Spinner() {
  return <div className="spin" style={{ width:18, height:18, border:`2px solid var(--border-2)`, borderTopColor:T.canyon, borderRadius:'50%' }} />
}

export function EmptyState({ icon, title, sub, action }: { icon: string; title: string; sub: string; action?: React.ReactNode }) {
  return (
    <div style={{ textAlign:'center', padding:'52px 24px' }}>
      <div style={{ fontSize:40, marginBottom:14, opacity:0.3 }}>{icon}</div>
      <div style={{ fontSize:15, fontWeight:700, fontFamily:T.fontDisplay, color:T.textSecondary, marginBottom:6 }}>{title}</div>
      <div style={{ fontSize:13, color:T.textMuted, maxWidth:320, margin:'0 auto', lineHeight:1.55 }}>{sub}</div>
      {action ? <div style={{ marginTop: 22, display: 'flex', justifyContent: 'center' }}>{action}</div> : null}
    </div>
  )
}

const skelPulse: React.CSSProperties = {
  background: `linear-gradient(90deg, ${T.surface3} 0%, ${T.surface4} 50%, ${T.surface3} 100%)`,
  backgroundSize: '200% 100%',
  animation: 'dsShimmer 1.1s ease-in-out infinite',
}

export function SkeletonBox({ h, w = '100%', r = 8 }: { h: number; w?: number | string; r?: number }) {
  return (
    <div className="ds-skel" aria-hidden style={{ height: h, width: w, borderRadius: r, ...skelPulse }} />
  )
}

export function DashboardKpiSkeleton() {
  return (
    <div className="ds-grid-kpi" aria-hidden>
      {[0, 1, 2, 3].map(i => (
        <div key={i} style={{ ...card(), minHeight: 120, display: 'flex', flexDirection: 'column', gap: 10, justifyContent: 'flex-end' }}>
          <SkeletonBox h={10} w="45%" r={4} />
          <SkeletonBox h={28} w="55%" r={6} />
          <SkeletonBox h={12} w="70%" r={4} />
        </div>
      ))}
    </div>
  )
}

export function QueryErrorBanner({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <div
      role="alert"
      style={{
        ...glass,
        borderRadius: 'var(--r-lg)',
        padding: '14px 18px',
        marginBottom: 18,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 14,
        flexWrap: 'wrap',
        border: `1px solid color-mix(in srgb, ${T.canyon} 35%, transparent)`,
        background: `color-mix(in srgb, ${T.canyonDim} 40%, transparent)`,
      }}
    >
      <span style={{ fontSize: 13, color: T.textPrimary, fontWeight: 600 }}>{message}</span>
      <button
        type="button"
        onClick={onRetry}
        style={{
          padding: '8px 16px',
          borderRadius: T.rSm,
          border: 'none',
          background: T.canyon,
          color: T.offWhite,
          fontWeight: 700,
          fontSize: 13,
          cursor: 'pointer',
          fontFamily: T.fontBody,
          boxShadow: 'var(--shadow-canyon)',
        }}
      >
        Retry
      </button>
    </div>
  )
}

export function TableDensityToggle({
  value,
  onChange,
}: {
  value: 'comfortable' | 'compact'
  onChange: (v: 'comfortable' | 'compact') => void
}) {
  const btn = (active: boolean): React.CSSProperties => ({
    padding: '5px 10px',
    borderRadius: 6,
    border: `1px solid ${active ? T.canyon : T.border2}`,
    background: active ? T.canyonDim : T.surface2,
    color: active ? T.canyon : T.textMuted,
    fontSize: 11,
    fontWeight: 700,
    fontFamily: T.fontDisplay,
    letterSpacing: 0.4,
    textTransform: 'uppercase' as const,
    cursor: 'pointer',
  })
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }} role="group" aria-label="Table density">
      <span style={{ fontSize: 11, color: T.textMuted, fontWeight: 600 }}>Density</span>
      <button type="button" aria-pressed={value === 'comfortable'} style={btn(value === 'comfortable')} onClick={() => onChange('comfortable')}>
        Comfortable
      </button>
      <button type="button" aria-pressed={value === 'compact'} style={btn(value === 'compact')} onClick={() => onChange('compact')}>
        Compact
      </button>
    </div>
  )
}

export const CHART_COLORS = [
  'var(--chart-1)', 'var(--chart-2)', 'var(--chart-3)', 'var(--chart-4)', 'var(--chart-5)', 'var(--chart-6)', 'var(--chart-7)',
]

// Keep these for any code that imports them
export const useTheme = () => T
